import { useEffect } from "react";

// Per-page SEO manager. Updates the document head on every route: title,
// meta description, canonical, robots, Open Graph, Twitter cards and any
// page-specific JSON-LD schemas. Tags are marked data-managed="seo" so each
// page fully overwrites the previous page's set.

const SITE_NAME = "IT Device Services";
const DEFAULT_IMAGE_PATH = "/assets/hero-bg.jpg";

const upsertTag = (selector, create, content) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  content(el);
  return el;
};

const upsertMeta = (attr, key, value) => {
  upsertTag(
    `meta[${attr}="${key}"]`,
    () => {
      const m = document.createElement("meta");
      m.setAttribute(attr, key);
      return m;
    },
    (el) => el.setAttribute("content", value)
  );
};

const Seo = ({ title, description, path, type = "website", image, noindex = false, schemas = [], article = null }) => {
  useEffect(() => {
    const origin = window.location.origin;
    const pagePath = path || window.location.pathname;
    const url = origin + pagePath;
    const imageUrl = origin + (image || DEFAULT_IMAGE_PATH);
    const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;

    document.title = fullTitle;
    upsertMeta("name", "description", description);
    upsertMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1");
    upsertMeta("name", "author", SITE_NAME);
    upsertMeta("name", "publisher", SITE_NAME);

    upsertMeta("property", "og:site_name", SITE_NAME);
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:title", fullTitle);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:image", imageUrl);
    upsertMeta("property", "og:image:alt", fullTitle);
    upsertMeta("property", "og:locale", "en_US");

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:site", "@itdeviceservices");
    upsertMeta("name", "twitter:title", fullTitle);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", imageUrl);

    // Article metadata (blog posts). Cleared on non-article pages so tags do
    // not linger when navigating away.
    document.head.querySelectorAll('meta[data-managed="seo-article"]').forEach((m) => m.remove());
    if (type === "article" && article) {
      const addArticle = (property, value) => {
        if (!value) return;
        const m = document.createElement("meta");
        m.setAttribute("property", property);
        m.setAttribute("content", value);
        m.setAttribute("data-managed", "seo-article");
        document.head.appendChild(m);
      };
      addArticle("article:published_time", article.publishedTime);
      addArticle("article:modified_time", article.modifiedTime || article.publishedTime);
      addArticle("article:section", article.section);
      addArticle("article:author", article.author || SITE_NAME);
    }

    upsertTag(
      'link[rel="canonical"]',
      () => {
        const l = document.createElement("link");
        l.setAttribute("rel", "canonical");
        return l;
      },
      (el) => el.setAttribute("href", url)
    );

    // hreflang self-reference plus x-default (single language site). Rebuilt
    // each render so it always matches the current URL.
    document.head.querySelectorAll('link[data-managed="seo-hreflang"]').forEach((l) => l.remove());
    ["en-us", "x-default"].forEach((lang) => {
      const l = document.createElement("link");
      l.setAttribute("rel", "alternate");
      l.setAttribute("hreflang", lang);
      l.setAttribute("href", url);
      l.setAttribute("data-managed", "seo-hreflang");
      document.head.appendChild(l);
    });

    // Page-specific JSON-LD (breadcrumbs, Service, BlogPosting...). The
    // static Organization/WebSite schemas live in index.html; visible FAQ
    // JSON-LD is rendered by FAQSection in the body.
    document.head.querySelectorAll('script[data-managed="seo"]').forEach((s) => s.remove());
    schemas.filter(Boolean).forEach((schema) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute("data-managed", "seo");
      s.textContent = JSON.stringify(schema);
      document.head.appendChild(s);
    });
  }, [title, description, path, type, image, noindex, article, JSON.stringify(schemas)]);

  return null;
};

export default Seo;
